import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, AlbLoadBalancer, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'load-balancers' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'elb5xx',    label: 'ELB 5XX'    },
  { key: 'target5xx', label: 'Target 5XX' },
  { key: 'respTime',  label: 'Resp ms'    },
  { key: 'unhealthy', label: 'Unhealthy'  },
];

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['scheme', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'       },
  { value: 'name-desc',       label: 'Name Z → A'       },
  { value: 'monitored-first', label: 'Monitored first'  },
];

const FACETS: FacetDef[] = [
  {
    key: 'scheme',
    label: 'Scheme',
    options: [
      { value: 'internet-facing', label: 'Internet-facing' },
      { value: 'internal',        label: 'Internal'        },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * ALB monitoring is at the load-balancer level (CloudWatch LoadBalancer dimension) —
 * ELB/target 5XX rates, target response time, and unhealthy host count. The monitored
 * resource identifier is the load-balancer name (e.g. app/my-lb/abc123). Per-target-group
 * breakdown is not supported in this version.
 */
@Component({
  selector: 'app-alb-detail',
  templateUrl: './alb-detail.component.html',
})
export class AlbDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  loadBalancers: AlbLoadBalancer[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',       label: 'Overview'       },
    { id: 'load-balancers', label: 'Load Balancers' },
    { id: 'activity',       label: 'Activity'       },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'alb', name: 'ALB', icon: 'network', route: '/cloud/alb', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('alb').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the load-balancer list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getAlbLoadBalancers(bypass).subscribe(loadBalancers => {
      this.loadBalancers = loadBalancers;
      this.loading       = false;
      this.refreshing    = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'ALB'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'load-balancers';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts (standardized: total + monitored, then service-specific) ──

  get totalCount():          number { return this.loadBalancers.length; }
  get monitoredCount():      number { return this.loadBalancers.filter(lb => this.isMonitored(lb.name)).length; }
  get internetFacingCount(): number { return this.loadBalancers.filter(lb => lb.scheme === 'internet-facing').length; }
  get activeCount():         number { return this.loadBalancers.filter(lb => lb.state === 'active').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredLoadBalancers(): AlbLoadBalancer[] {
    let result = this.loadBalancers;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(item => item.name.toLowerCase().includes(q));

    const schemes = this.filters.facets['scheme'] ?? [];
    if (schemes.length) {
      result = result.filter(item => schemes.includes(item.scheme));
    }

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(item => {
        const mon = this.isMonitored(item.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(loadBalancers: AlbLoadBalancer[], sort: string): AlbLoadBalancer[] {
    return [...loadBalancers].sort((a, b) => {
      switch (sort) {
        case 'name-desc':       return b.name.localeCompare(a.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a.name) ? 0 : 1;
          const bM = this.isMonitored(b.name) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default: return a.name.localeCompare(b.name); // name-asc
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  schemeLabel(lb: AlbLoadBalancer): string {
    return lb.scheme === 'internet-facing' ? 'Internet-facing' : 'Internal';
  }

  stateClass(state: string): string {
    return state === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(loadBalancerName: string): boolean { return this.cloud.isMonitored('alb', loadBalancerName); }

  toggleMonitoring(loadBalancerName: string): void {
    const current = this.cloud.isMonitored('alb', loadBalancerName);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('alb', loadBalancerName, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(loadBalancerName: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: loadBalancerName }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(loadBalancerName: string): void  { this.drawerItemId = loadBalancerName; }
  closeItemSettings(): void                         { this.drawerItemId = null; }

  itemOverrideSummary(loadBalancerName: string): string {
    return this.cloud.getItemOverrideSummary('alb', loadBalancerName);
  }
}
